import { useState, useEffect } from 'react';
import { Truck, Plus, Trash2, Edit2, ChevronDown, ChevronRight, Settings, Zap, Package, ExternalLink, CheckCircle, XCircle } from 'lucide-react';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';

// ─── Types ──────────────────────────────────────────────────────────

interface ShippingRate {
  id: string;
  name: string;
  price: number;
  min_order_amount: number | null;
  max_order_amount: number | null;
  min_weight: number | null;
  max_weight: number | null;
  estimated_days_min: number | null;
  estimated_days_max: number | null;
}

interface ShippingZone {
  id: string;
  name: string;
  countries: string;
  states: string;
  is_active: boolean;
  rates: ShippingRate[];
}

interface ZoneFormData {
  name: string;
  countries: string;
  states: string;
  is_active: boolean;
}

interface RateFormData {
  name: string;
  price: string;
  min_order_amount: string;
  max_order_amount: string;
  min_weight: string;
  max_weight: string;
  estimated_days_min: string;
  estimated_days_max: string;
}

interface ShipFrom {
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface GeneralConfig {
  ship_from: ShipFrom;
  default_length_in: number;
  default_width_in: number;
  default_height_in: number;
  default_weight_oz: number;
  free_shipping_threshold: number;
}

interface USPSConfig {
  consumer_id: string;
  consumer_secret: string;
  enabled: boolean;
}

interface FedExConfig {
  client_id: string;
  client_secret: string;
  account_number: string;
  enabled: boolean;
  sandbox: boolean;
}

type Tab = 'general' | 'carriers' | 'flat-rates';

// ─── Component ──────────────────────────────────────────────────────

export default function Shipping() {
  const [activeTab, setActiveTab] = useState<Tab>('general');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // General config
  const [generalConfig, setGeneralConfig] = useState<GeneralConfig>({
    ship_from: { name: '', street: '', city: '', state: 'MI', zip: '', country: 'US' },
    default_length_in: 9,
    default_width_in: 6,
    default_height_in: 3,
    default_weight_oz: 4,
    free_shipping_threshold: 0,
  });

  // Carrier configs
  const [uspsConfig, setUspsConfig] = useState<USPSConfig>({ consumer_id: '', consumer_secret: '', enabled: false });
  const [fedexConfig, setFedexConfig] = useState<FedExConfig>({ client_id: '', client_secret: '', account_number: '', enabled: false, sandbox: false });

  // Flat rates
  const [zones, setZones] = useState<ShippingZone[]>([]);
  const [expandedZones, setExpandedZones] = useState<Set<string>>(new Set());

  // Modals
  const [zoneModal, setZoneModal] = useState<{ isOpen: boolean; zoneId: string | null; mode: 'create' | 'edit' }>({ isOpen: false, zoneId: null, mode: 'create' });
  const [zoneFormData, setZoneFormData] = useState<ZoneFormData>({ name: '', countries: '', states: '', is_active: true });
  const [rateModal, setRateModal] = useState<{ isOpen: boolean; zoneId: string | null; rateId: string | null; mode: 'create' | 'edit' }>({ isOpen: false, zoneId: null, rateId: null, mode: 'create' });
  const [rateFormData, setRateFormData] = useState<RateFormData>({ name: '', price: '', min_order_amount: '', max_order_amount: '', min_weight: '', max_weight: '', estimated_days_min: '', estimated_days_max: '' });
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; type: 'zone' | 'rate'; id: string | null; name: string }>({ isOpen: false, type: 'zone', id: null, name: '' });

  const [isProcessing, setIsProcessing] = useState(false);
  const [isTesting, setIsTesting] = useState<'usps' | 'fedex' | null>(null);
  const [testResult, setTestResult] = useState<{ carrier: string; success: boolean; message: string } | null>(null);

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  async function fetchAll() {
    try {
      setIsLoading(true);
      setError(null);
      const [configRes, zonesRes] = await Promise.all([
        api.get<{ shipping_config: GeneralConfig; usps_config: USPSConfig; fedex_config: FedExConfig }>('/admin/shipping/config'),
        api.get<{ zones: ShippingZone[] }>('/admin/shipping'),
      ]);
      setGeneralConfig(configRes.shipping_config);
      setUspsConfig(configRes.usps_config);
      setFedexConfig(configRes.fedex_config);
      setZones(zonesRes.zones);
    } catch (err) {
      setError('Failed to load shipping settings.');
      console.error('Error fetching shipping:', err);
    } finally {
      setIsLoading(false);
    }
  }

  // ─── General Config Handlers ────────────────────────────────────

  const updateShipFrom = (field: keyof ShipFrom, value: string) => {
    setGeneralConfig(prev => ({ ...prev, ship_from: { ...prev.ship_from, [field]: value } }));
  };

  async function saveGeneralConfig() {
    try {
      setIsProcessing(true);
      setError(null);
      await api.put('/admin/shipping/config/general', generalConfig);
      setSuccess('General settings saved.');
    } catch (err) {
      setError('Failed to save general settings.');
    } finally {
      setIsProcessing(false);
    }
  }

  // ─── Carrier Config Handlers ────────────────────────────────────

  async function saveUSPS() {
    try {
      setIsProcessing(true);
      setError(null);
      await api.put('/admin/shipping/config/usps', uspsConfig);
      setSuccess('USPS settings saved.');
    } catch (err) {
      setError('Failed to save USPS settings.');
    } finally {
      setIsProcessing(false);
    }
  }

  async function saveFedEx() {
    try {
      setIsProcessing(true);
      setError(null);
      await api.put('/admin/shipping/config/fedex', fedexConfig);
      setSuccess('FedEx settings saved.');
    } catch (err) {
      setError('Failed to save FedEx settings.');
    } finally {
      setIsProcessing(false);
    }
  }

  async function testCarrier(carrier: 'usps' | 'fedex') {
    setIsTesting(carrier);
    setTestResult(null);
    try {
      const res = await api.post<{ success: boolean; message: string; error?: string }>(`/admin/shipping/config/test-${carrier}`);
      setTestResult({ carrier, success: true, message: res.message || 'Connection successful!' });
    } catch (err: any) {
      setTestResult({ carrier, success: false, message: err.message || 'Connection test failed.' });
    } finally {
      setIsTesting(null);
    }
  }

  // ─── Flat Rate Handlers (existing logic) ────────────────────────

  function toggleZoneExpansion(zoneId: string) {
    const newExpanded = new Set(expandedZones);
    if (newExpanded.has(zoneId)) newExpanded.delete(zoneId);
    else newExpanded.add(zoneId);
    setExpandedZones(newExpanded);
  }

  function openZoneModal(mode: 'create' | 'edit', zoneId: string | null = null) {
    if (mode === 'edit' && zoneId) {
      const zone = zones.find(z => z.id === zoneId);
      if (zone) setZoneFormData({ name: zone.name, countries: zone.countries, states: zone.states, is_active: zone.is_active });
    } else {
      setZoneFormData({ name: '', countries: '', states: '', is_active: true });
    }
    setZoneModal({ isOpen: true, zoneId, mode });
  }

  function closeZoneModal() { setZoneModal({ isOpen: false, zoneId: null, mode: 'create' }); }

  async function handleSaveZone() {
    try {
      setIsProcessing(true);
      if (zoneModal.mode === 'edit' && zoneModal.zoneId) {
        await api.put(`/admin/shipping/zones/${zoneModal.zoneId}`, zoneFormData);
      } else {
        await api.post('/admin/shipping/zones', zoneFormData);
      }
      await fetchAll();
      closeZoneModal();
    } catch (err) {
      setError('Failed to save zone.');
    } finally {
      setIsProcessing(false);
    }
  }

  function openRateModal(zoneId: string, mode: 'create' | 'edit', rateId: string | null = null) {
    if (mode === 'edit' && rateId) {
      const zone = zones.find(z => z.id === zoneId);
      const rate = zone?.rates.find(r => r.id === rateId);
      if (rate) {
        setRateFormData({
          name: rate.name, price: rate.price.toString(),
          min_order_amount: rate.min_order_amount?.toString() || '', max_order_amount: rate.max_order_amount?.toString() || '',
          min_weight: rate.min_weight?.toString() || '', max_weight: rate.max_weight?.toString() || '',
          estimated_days_min: rate.estimated_days_min?.toString() || '', estimated_days_max: rate.estimated_days_max?.toString() || '',
        });
      }
    } else {
      setRateFormData({ name: '', price: '', min_order_amount: '', max_order_amount: '', min_weight: '', max_weight: '', estimated_days_min: '', estimated_days_max: '' });
    }
    setRateModal({ isOpen: true, zoneId, rateId, mode });
  }

  function closeRateModal() { setRateModal({ isOpen: false, zoneId: null, rateId: null, mode: 'create' }); }

  async function handleSaveRate() {
    try {
      setIsProcessing(true);
      const payload = {
        zone_id: rateModal.zoneId, name: rateFormData.name, price: parseFloat(rateFormData.price),
        min_order_amount: rateFormData.min_order_amount ? parseFloat(rateFormData.min_order_amount) : null,
        max_order_amount: rateFormData.max_order_amount ? parseFloat(rateFormData.max_order_amount) : null,
        min_weight: rateFormData.min_weight ? parseFloat(rateFormData.min_weight) : null,
        max_weight: rateFormData.max_weight ? parseFloat(rateFormData.max_weight) : null,
        estimated_days_min: rateFormData.estimated_days_min ? parseInt(rateFormData.estimated_days_min) : null,
        estimated_days_max: rateFormData.estimated_days_max ? parseInt(rateFormData.estimated_days_max) : null,
      };
      if (rateModal.mode === 'edit' && rateModal.rateId) {
        await api.put(`/admin/shipping/rates/${rateModal.rateId}`, payload);
      } else {
        await api.post('/admin/shipping/rates', payload);
      }
      await fetchAll();
      closeRateModal();
    } catch (err) {
      setError('Failed to save rate.');
    } finally {
      setIsProcessing(false);
    }
  }

  function openDeleteModal(type: 'zone' | 'rate', id: string, name: string) {
    setDeleteModal({ isOpen: true, type, id, name });
  }

  function closeDeleteModal() { setDeleteModal({ isOpen: false, type: 'zone', id: null, name: '' }); }

  async function handleDelete() {
    if (!deleteModal.id) return;
    try {
      setIsProcessing(true);
      if (deleteModal.type === 'zone') await api.delete(`/admin/shipping/zones/${deleteModal.id}`);
      else await api.delete(`/admin/shipping/rates/${deleteModal.id}`);
      await fetchAll();
      closeDeleteModal();
    } catch (err) {
      setError(`Failed to delete ${deleteModal.type}.`);
    } finally {
      setIsProcessing(false);
    }
  }

  function formatEstimatedDays(rate: ShippingRate): string {
    if (rate.estimated_days_min && rate.estimated_days_max) return `${rate.estimated_days_min}-${rate.estimated_days_max} days`;
    if (rate.estimated_days_min) return `${rate.estimated_days_min}+ days`;
    if (rate.estimated_days_max) return `Up to ${rate.estimated_days_max} days`;
    return 'Not specified';
  }

  // ─── Render ─────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading shipping settings...</span>
      </div>
    );
  }

  const tabs: { key: Tab; label: string; icon: typeof Settings }[] = [
    { key: 'general', label: 'General', icon: Settings },
    { key: 'carriers', label: 'Carriers', icon: Zap },
    { key: 'flat-rates', label: 'Flat Rates', icon: Package },
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Truck className="w-8 h-8 text-gray-700" aria-hidden="true" />
        <h1 className="text-3xl font-bold text-gray-900">Shipping</h1>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg" role="status">
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6" aria-label="Shipping settings tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                aria-current={activeTab === tab.key ? 'page' : undefined}
              >
                <Icon className="w-4 h-4" aria-hidden="true" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ─── Tab: General ─────────────────────────────────────────── */}
      {activeTab === 'general' && (
        <div className="space-y-6 max-w-2xl">
          <Card title="Ship From Address" padding="md">
            <div className="space-y-4">
              <Input label="Business Name" value={generalConfig.ship_from.name} onChange={e => updateShipFrom('name', e.target.value)} placeholder="Your Business Name" />
              <Input label="Street Address" value={generalConfig.ship_from.street} onChange={e => updateShipFrom('street', e.target.value)} placeholder="123 Main St" />
              <div className="grid grid-cols-2 gap-4">
                <Input label="City" value={generalConfig.ship_from.city} onChange={e => updateShipFrom('city', e.target.value)} placeholder="Dearborn" />
                <Input label="State" value={generalConfig.ship_from.state} onChange={e => updateShipFrom('state', e.target.value)} placeholder="MI" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="ZIP Code" value={generalConfig.ship_from.zip} onChange={e => updateShipFrom('zip', e.target.value)} placeholder="48120" />
                <Input label="Country" value={generalConfig.ship_from.country} onChange={e => updateShipFrom('country', e.target.value)} placeholder="US" />
              </div>
            </div>
          </Card>

          <Card title="Default Package Dimensions" padding="md">
            <p className="text-sm text-gray-500 mb-4">Used when calculating carrier rates if product-specific dimensions aren't set.</p>
            <div className="grid grid-cols-3 gap-4">
              <Input label="Length (in)" type="number" step="0.1" value={generalConfig.default_length_in.toString()} onChange={e => setGeneralConfig(prev => ({ ...prev, default_length_in: parseFloat(e.target.value) || 0 }))} />
              <Input label="Width (in)" type="number" step="0.1" value={generalConfig.default_width_in.toString()} onChange={e => setGeneralConfig(prev => ({ ...prev, default_width_in: parseFloat(e.target.value) || 0 }))} />
              <Input label="Height (in)" type="number" step="0.1" value={generalConfig.default_height_in.toString()} onChange={e => setGeneralConfig(prev => ({ ...prev, default_height_in: parseFloat(e.target.value) || 0 }))} />
            </div>
          </Card>

          <Card title="Defaults & Free Shipping" padding="md">
            <div className="space-y-4">
              <Input
                label="Default Weight (oz)"
                type="number"
                step="0.1"
                value={generalConfig.default_weight_oz.toString()}
                onChange={e => setGeneralConfig(prev => ({ ...prev, default_weight_oz: parseFloat(e.target.value) || 0 }))}
                helperText="Used for items without a specified weight"
              />
              <Input
                label="Free Shipping Threshold ($)"
                type="number"
                step="0.01"
                value={generalConfig.free_shipping_threshold.toString()}
                onChange={e => setGeneralConfig(prev => ({ ...prev, free_shipping_threshold: parseFloat(e.target.value) || 0 }))}
                helperText="Orders above this amount qualify for free shipping (0 = disabled)"
              />
            </div>
          </Card>

          <Button variant="primary" onClick={saveGeneralConfig} isLoading={isProcessing}>Save General Settings</Button>
        </div>
      )}

      {/* ─── Tab: Carriers ────────────────────────────────────────── */}
      {activeTab === 'carriers' && (
        <div className="space-y-6 max-w-2xl">
          {/* USPS */}
          <Card title="USPS" padding="md">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={uspsConfig.enabled}
                      onChange={e => setUspsConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Enabled</span>
                  </label>
                  <Badge variant={uspsConfig.consumer_id ? 'green' : 'gray'}>
                    {uspsConfig.consumer_id ? 'Configured' : 'Not configured'}
                  </Badge>
                </div>
                <a
                  href="https://developer.usps.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  Developer portal <ExternalLink className="w-3 h-3" aria-hidden="true" />
                </a>
              </div>
              <Input
                label="Consumer ID"
                value={uspsConfig.consumer_id}
                onChange={e => setUspsConfig(prev => ({ ...prev, consumer_id: e.target.value }))}
                placeholder="Enter your USPS Consumer ID"
              />
              <Input
                label="Consumer Secret"
                type="password"
                value={uspsConfig.consumer_secret}
                onChange={e => setUspsConfig(prev => ({ ...prev, consumer_secret: e.target.value }))}
                placeholder="Enter your USPS Consumer Secret"
              />
              {testResult?.carrier === 'usps' && (
                <div className={`flex items-center gap-2 text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                  {testResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  {testResult.message}
                </div>
              )}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => testCarrier('usps')} isLoading={isTesting === 'usps'}>
                  Test Connection
                </Button>
                <Button variant="primary" onClick={saveUSPS} isLoading={isProcessing && isTesting !== 'usps'}>
                  Save USPS
                </Button>
              </div>
            </div>
          </Card>

          {/* FedEx */}
          <Card title="FedEx" padding="md">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={fedexConfig.enabled}
                      onChange={e => setFedexConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Enabled</span>
                  </label>
                  <Badge variant={fedexConfig.client_id ? 'green' : 'gray'}>
                    {fedexConfig.client_id ? 'Configured' : 'Not configured'}
                  </Badge>
                </div>
                <a
                  href="https://developer.fedex.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  Developer portal <ExternalLink className="w-3 h-3" aria-hidden="true" />
                </a>
              </div>
              <Input
                label="Client ID"
                value={fedexConfig.client_id}
                onChange={e => setFedexConfig(prev => ({ ...prev, client_id: e.target.value }))}
                placeholder="Enter FedEx Client ID"
              />
              <Input
                label="Client Secret"
                type="password"
                value={fedexConfig.client_secret}
                onChange={e => setFedexConfig(prev => ({ ...prev, client_secret: e.target.value }))}
                placeholder="Enter FedEx Client Secret"
              />
              <Input
                label="Account Number"
                value={fedexConfig.account_number}
                onChange={e => setFedexConfig(prev => ({ ...prev, account_number: e.target.value }))}
                placeholder="Enter FedEx Account Number"
              />
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={fedexConfig.sandbox}
                  onChange={e => setFedexConfig(prev => ({ ...prev, sandbox: e.target.checked }))}
                  className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                />
                <span className="text-sm font-medium text-gray-700">Sandbox mode</span>
                {fedexConfig.sandbox && (
                  <Badge variant="yellow">Testing</Badge>
                )}
              </label>
              {testResult?.carrier === 'fedex' && (
                <div className={`flex items-center gap-2 text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                  {testResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  {testResult.message}
                </div>
              )}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => testCarrier('fedex')} isLoading={isTesting === 'fedex'}>
                  Test Connection
                </Button>
                <Button variant="primary" onClick={saveFedEx} isLoading={isProcessing && isTesting !== 'fedex'}>
                  Save FedEx
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ─── Tab: Flat Rates ──────────────────────────────────────── */}
      {activeTab === 'flat-rates' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => openZoneModal('create')} aria-label="Add new shipping zone">
              <Plus className="w-5 h-5 mr-2" aria-hidden="true" />
              Add Zone
            </Button>
          </div>

          {zones.length === 0 ? (
            <Card padding="lg">
              <div className="text-center py-12">
                <Truck className="w-12 h-12 mx-auto mb-3 text-gray-400" aria-hidden="true" />
                <p className="text-lg text-gray-600">No shipping zones configured</p>
                <p className="text-sm text-gray-500 mt-1">Create your first shipping zone to get started</p>
              </div>
            </Card>
          ) : (
            zones.map((zone) => (
              <Card key={zone.id} padding="none">
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => toggleZoneExpansion(zone.id)}
                      className="flex items-center gap-3 flex-1 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                      aria-expanded={expandedZones.has(zone.id)}
                      aria-controls={`zone-${zone.id}-rates`}
                    >
                      {expandedZones.has(zone.id) ? (
                        <ChevronDown className="w-5 h-5 text-gray-500" aria-hidden="true" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500" aria-hidden="true" />
                      )}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{zone.name}</h3>
                        <p className="text-sm text-gray-600">
                          {zone.countries && `Countries: ${zone.countries}`}
                          {zone.countries && zone.states && ' \u2022 '}
                          {zone.states && `States: ${zone.states}`}
                        </p>
                      </div>
                    </button>
                    <div className="flex items-center gap-3">
                      <Badge variant={zone.is_active ? 'green' : 'gray'}>{zone.is_active ? 'Active' : 'Inactive'}</Badge>
                      <Button variant="outline" size="sm" onClick={() => openZoneModal('edit', zone.id)} aria-label={`Edit zone ${zone.name}`}>
                        <Edit2 className="w-4 h-4" aria-hidden="true" />
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => openDeleteModal('zone', zone.id, zone.name)} aria-label={`Delete zone ${zone.name}`}>
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </div>
                </div>

                {expandedZones.has(zone.id) && (
                  <div id={`zone-${zone.id}-rates`} className="border-t border-gray-200 bg-gray-50 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-900">Shipping Rates</h4>
                      <Button variant="outline" size="sm" onClick={() => openRateModal(zone.id, 'create')} aria-label={`Add rate to ${zone.name}`}>
                        <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
                        Add Rate
                      </Button>
                    </div>

                    {zone.rates.length === 0 ? (
                      <p className="text-gray-500 text-center py-6">No rates configured for this zone</p>
                    ) : (
                      <div className="space-y-3">
                        {zone.rates.map((rate) => (
                          <div key={rate.id} className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{rate.name}</p>
                              <div className="text-sm text-gray-600 mt-1 space-y-1">
                                <p>Price: {formatCurrency(rate.price)}</p>
                                {(rate.min_order_amount || rate.max_order_amount) && (
                                  <p>Order: {rate.min_order_amount ? formatCurrency(rate.min_order_amount) : '$0'} - {rate.max_order_amount ? formatCurrency(rate.max_order_amount) : 'No max'}</p>
                                )}
                                {(rate.min_weight || rate.max_weight) && (
                                  <p>Weight: {rate.min_weight || 0} - {rate.max_weight || 'No max'} lbs</p>
                                )}
                                <p>Delivery: {formatEstimatedDays(rate)}</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => openRateModal(zone.id, 'edit', rate.id)} aria-label={`Edit rate ${rate.name}`}>
                                <Edit2 className="w-4 h-4" aria-hidden="true" />
                              </Button>
                              <Button variant="danger" size="sm" onClick={() => openDeleteModal('rate', rate.id, rate.name)} aria-label={`Delete rate ${rate.name}`}>
                                <Trash2 className="w-4 h-4" aria-hidden="true" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      )}

      {/* ─── Modals ───────────────────────────────────────────────── */}
      <Modal isOpen={zoneModal.isOpen} onClose={closeZoneModal} title={zoneModal.mode === 'create' ? 'Create Shipping Zone' : 'Edit Shipping Zone'}>
        <div className="space-y-4">
          <Input label="Zone Name" value={zoneFormData.name} onChange={e => setZoneFormData({ ...zoneFormData, name: e.target.value })} placeholder="United States" required aria-required="true" />
          <Input label="Countries" value={zoneFormData.countries} onChange={e => setZoneFormData({ ...zoneFormData, countries: e.target.value })} placeholder="US, CA (comma-separated)" helperText="Comma-separated country codes" />
          <Input label="States" value={zoneFormData.states} onChange={e => setZoneFormData({ ...zoneFormData, states: e.target.value })} placeholder="CA, NY, TX (comma-separated)" helperText="Comma-separated state codes (optional)" />
          <div className="flex items-center gap-3">
            <input type="checkbox" id="zone_is_active" checked={zoneFormData.is_active} onChange={e => setZoneFormData({ ...zoneFormData, is_active: e.target.checked })} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500" />
            <label htmlFor="zone_is_active" className="text-sm font-medium text-gray-700">Zone is active</label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={closeZoneModal} disabled={isProcessing}>Cancel</Button>
            <Button variant="primary" onClick={handleSaveZone} isLoading={isProcessing}>{zoneModal.mode === 'create' ? 'Create Zone' : 'Update Zone'}</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={rateModal.isOpen} onClose={closeRateModal} title={rateModal.mode === 'create' ? 'Create Shipping Rate' : 'Edit Shipping Rate'}>
        <div className="space-y-4">
          <Input label="Rate Name" value={rateFormData.name} onChange={e => setRateFormData({ ...rateFormData, name: e.target.value })} placeholder="Standard Shipping" required aria-required="true" />
          <Input label="Price" type="number" value={rateFormData.price} onChange={e => setRateFormData({ ...rateFormData, price: e.target.value })} placeholder="5.99" step="0.01" required aria-required="true" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Min Order Amount" type="number" value={rateFormData.min_order_amount} onChange={e => setRateFormData({ ...rateFormData, min_order_amount: e.target.value })} placeholder="0.00" step="0.01" />
            <Input label="Max Order Amount" type="number" value={rateFormData.max_order_amount} onChange={e => setRateFormData({ ...rateFormData, max_order_amount: e.target.value })} placeholder="No max" step="0.01" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Min Weight (lbs)" type="number" value={rateFormData.min_weight} onChange={e => setRateFormData({ ...rateFormData, min_weight: e.target.value })} placeholder="0" step="0.01" />
            <Input label="Max Weight (lbs)" type="number" value={rateFormData.max_weight} onChange={e => setRateFormData({ ...rateFormData, max_weight: e.target.value })} placeholder="No max" step="0.01" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Est. Days (Min)" type="number" value={rateFormData.estimated_days_min} onChange={e => setRateFormData({ ...rateFormData, estimated_days_min: e.target.value })} placeholder="3" step="1" />
            <Input label="Est. Days (Max)" type="number" value={rateFormData.estimated_days_max} onChange={e => setRateFormData({ ...rateFormData, estimated_days_max: e.target.value })} placeholder="5" step="1" />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={closeRateModal} disabled={isProcessing}>Cancel</Button>
            <Button variant="primary" onClick={handleSaveRate} isLoading={isProcessing}>{rateModal.mode === 'create' ? 'Create Rate' : 'Update Rate'}</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={deleteModal.isOpen} onClose={closeDeleteModal} title={`Delete ${deleteModal.type === 'zone' ? 'Zone' : 'Rate'}`}>
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete <strong>{deleteModal.name}</strong>?
            {deleteModal.type === 'zone' && ' All rates in this zone will also be deleted.'}
            {' '}This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={closeDeleteModal} disabled={isProcessing}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete} isLoading={isProcessing}>Delete {deleteModal.type === 'zone' ? 'Zone' : 'Rate'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
